import { formEditAddressClassName } from 'components/FormAddTransaction/components/FormEditAddress';
import { formAddBankAccountClassName } from 'components/FormEditReceiver/components/FormAddNewBankAccount';
import { formAddEwalletAccountClassName } from 'components/FormEditReceiver/components/FormAddNewEwalletAccount';
import { LogUtil } from 'components/common/LogUtil';
import useGetElementByClassName from 'hooks/useGetElementByClassName';
import { isEmpty, isObject } from 'lodash';
import { compareObject } from '../helpers/compares';
import { transformServiceAlertsData } from '../helpers/transforms';
import { ServiceAlertsStoreModal } from '../model/ServiceAlertsLocalStore';
import {
  SERVICE_ALERTS_TYPE,
  ServiceAlertsModel,
} from '../model/ServiceAlertsModel';
import useServiceAlertsModal from '../useServiceAlertsModal';

const TAG = 'service-alerts-listener';

const serviceAlertsStoreVariant: any = {
  countryCode: 'serviceAlertsByCountryCode',
  payoutMethod: 'serviceAlertsByPayoutMethod',
  payoutMethodItem: 'serviceAlertsByPayoutMethodItem',
  payoutMethodItemChild: 'serviceAlertsByPayoutMethodItemChild',
  currencyCode1: 'serviceAlertsByCurrencyCode1',
  currencyCode2: 'serviceAlertsByCurrencyCode2',
  rangeOfCurrencyCode1: 'serviceAlertsByRangeOfCurrencyCode1',
  rangeOfCurrencyCode2: 'serviceAlertsByRangeOfCurrencyCode2',
  paymentMethod: 'serviceAlertsByPaymentMethod',
};

interface Props {
  variant: string;
  modalOnReset?: (serviceAlertsStore?: ServiceAlertsStoreModal) => void;
  modalOnClick?: (serviceAlertsStore?: ServiceAlertsStoreModal) => void;
}
const useServiceAlertsListener = ({
  variant,
  modalOnReset = () => {},
  modalOnClick = () => {},
}: Props) => {
  const { className: screenName1 } = useGetElementByClassName(
    formEditAddressClassName
  );
  const { className: screenName2 } = useGetElementByClassName(
    formAddBankAccountClassName
  );
  const { className: screenName3 } = useGetElementByClassName(
    formAddEwalletAccountClassName
  );

  const {
    isServiceAlertsModalShowing,
    currentModal,
    handleShowServiceAlertsModal,
    closeServiceAlertsModal,
  } = useServiceAlertsModal();

  const serviceAlertsStoreProp: string =
    serviceAlertsStoreVariant?.[variant] || '';

  const getCurrentServiceAlertsStore = ({
    serviceAlertsStore,
  }: {
    serviceAlertsStore: ServiceAlertsStoreModal | any;
  }) => {
    if (serviceAlertsStore) {
      return serviceAlertsStore?.[serviceAlertsStoreProp] || null;
    }

    return null;
  };

  const onCheck = ({
    serviceAlertsStore,
    isChange = false,
    isPreventShowServiceAlertsModal = false,
    onSuppressedShow,
  }: {
    serviceAlertsStore: ServiceAlertsStoreModal;
    isChange?: boolean;
    isPreventShowServiceAlertsModal?: boolean;
    onSuppressedShow?: () => void;
  }) => {
    const { message, type } =
      getCurrentServiceAlertsStore({ serviceAlertsStore }) || {};

    const isCloseServiceAlertsModal =
      isServiceAlertsModalShowing && isChange && !type;
    if (isCloseServiceAlertsModal) {
      if (!isPreventShowServiceAlertsModal) {
        LogUtil.log(TAG, `ACTION xxxx closeServiceAlertsModal xxxx ${variant}`);
        closeServiceAlertsModal();
      }

      if (modalOnClick) {
        modalOnClick(serviceAlertsStore);
      }
    }

    if (isPreventShowServiceAlertsModal) {
      // A real alert was withheld, not merely absent. Report it so the caller
      // can owe itself a re-show once the reason clears: the discount gate
      // suppresses on "discounts are busy" but only owed a re-show on an input
      // change, so a show suppressed while busy without an observed input
      // change was dropped for good. Reporting from here keeps the debt tied to
      // an attempted show, so an unchanged, already-dismissed alert (which
      // reaches no branch, hence no onCheck) still owes nothing.
      if (type && message && onSuppressedShow) {
        onSuppressedShow();
      }
      return;
    }
    const routesPreventShowServiceAlertsModal = [
      screenName1,
      screenName2,
      screenName3,
    ];
    const isHandleShowServiceAlertsModal =
      type &&
      message &&
      (!currentModal || isServiceAlertsModalShowing) &&
      !routesPreventShowServiceAlertsModal.find((screenName) => screenName);
    if (isHandleShowServiceAlertsModal) {
      LogUtil.log(
        TAG,
        `ACTION xxxx handleShowServiceAlertsModal xxxx ${variant}`
      );

      const getModalOnClick = () => {
        if (type === SERVICE_ALERTS_TYPE.DISABLE && modalOnReset)
          return () => modalOnReset(serviceAlertsStore);
        if (type === SERVICE_ALERTS_TYPE.DELAY && modalOnClick)
          return () => modalOnClick(serviceAlertsStore);
        return () => {};
      };
      handleShowServiceAlertsModal({
        message,
        type,
        onClick: getModalOnClick(),
      });
    }
  };

  const onGet = ({
    data,
    serviceAlertsStore,
    isPreventShowServiceAlertsModal,
    onSuppressedShow,
  }: {
    data: ServiceAlertsModel;
    serviceAlertsStore: ServiceAlertsStoreModal;
    isPreventShowServiceAlertsModal?: boolean;
    onSuppressedShow?: () => void;
  }) => {
    const newServiceAlertsStore = {
      ...serviceAlertsStore,
      [serviceAlertsStoreProp]: data,
    };

    onCheck({
      serviceAlertsStore: newServiceAlertsStore,
      isPreventShowServiceAlertsModal,
      onSuppressedShow,
    });

    return newServiceAlertsStore;
  };

  const onChange = ({
    data,
    serviceAlertsStore,
    isPreventShowServiceAlertsModal,
    onSuppressedShow,
  }: {
    data: ServiceAlertsModel;
    serviceAlertsStore: ServiceAlertsStoreModal;
    isPreventShowServiceAlertsModal?: boolean;
    onSuppressedShow?: () => void;
  }) => {
    const newServiceAlertsStore = {
      ...serviceAlertsStore,
      [serviceAlertsStoreProp]: data,
    };

    onCheck({
      serviceAlertsStore: newServiceAlertsStore,
      isChange: true,
      isPreventShowServiceAlertsModal,
      onSuppressedShow,
    });

    return newServiceAlertsStore;
  };

  const onListener = ({
    data = null,
    serviceAlertsStore,
    isPreventOnGet = false,
    isPreventShowServiceAlertsModal = false,
    isForceReshow = false,
    onSuppressedShow,
  }: {
    data: ServiceAlertsModel | null;
    serviceAlertsStore: ServiceAlertsStoreModal;
    isPreventOnGet?: boolean;
    isPreventShowServiceAlertsModal?: boolean;
    isForceReshow?: boolean;
    // Called when this run withheld a real, showable alert. The caller owes
    // itself a re-show once the suppressing reason clears.
    onSuppressedShow?: () => void;
  }) => {
    const dataTransform = transformServiceAlertsData(data);

    const currentServiceAlertsStore = getCurrentServiceAlertsStore({
      serviceAlertsStore,
    });

    // isPreventOnGet suppresses the popup on first data arrival, not the store
    // record. Skipping the record entirely is self-perpetuating: with an empty
    // slot, onChange and the force-reshow below are both gated on
    // !isEmpty(currentServiceAlertsStore), so the alert could never surface
    // later either. A range-level DELAY (the only isPreventOnGet caller) hit
    // this: its slot is nulled while no amount is entered, so the amount-entry
    // run is always its first get, and it stayed unrecorded and unshowable.
    // Record here, stay silent, and let the busy -> settled flush show it.
    const isGetNewestServiceAlerts =
      isObject(dataTransform) && isEmpty(currentServiceAlertsStore);
    if (isGetNewestServiceAlerts) {
      LogUtil.log(
        TAG,
        `onGet xxxx ${variant} xxxx data xxxx`,
        JSON.stringify(dataTransform)
      );
      return onGet({
        data: dataTransform,
        serviceAlertsStore,
        isPreventShowServiceAlertsModal:
          isPreventShowServiceAlertsModal || isPreventOnGet,
        onSuppressedShow,
      });
    }

    const isChangeNewestServiceAlertsStore =
      isObject(dataTransform) &&
      !isEmpty(currentServiceAlertsStore) &&
      !compareObject(dataTransform, currentServiceAlertsStore);
    if (isChangeNewestServiceAlertsStore) {
      LogUtil.log(
        TAG,
        `onChange xxxx ${variant} xxxx data xxxx`,
        JSON.stringify(dataTransform)
      );
      return onChange({
        data: dataTransform,
        serviceAlertsStore,
        isPreventShowServiceAlertsModal,
        onSuppressedShow,
      });
    }

    // Re-show a still-applicable alert when the user changed the currency/amount
    // inputs, even though the alert data itself is unchanged (so neither onGet
    // nor onChange fires). DISABLE remains suppressed by
    // isPreventShowServiceAlertsModal inside onCheck, so this only re-surfaces
    // DELAY alerts.
    const isForceReshowServiceAlerts =
      isForceReshow &&
      isObject(dataTransform) &&
      !isEmpty(currentServiceAlertsStore);
    if (isForceReshowServiceAlerts) {
      return onChange({
        data: dataTransform,
        serviceAlertsStore,
        isPreventShowServiceAlertsModal,
        onSuppressedShow,
      });
    }

    return serviceAlertsStore;
  };

  return { onListener };
};

export default useServiceAlertsListener;
